import React, { useState, useMemo, useCallback } from "react"
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Badge,
  Spinner,
} from "react-bootstrap"
import { Search, X, RefreshCw, AlertCircle, Phone, MapPin } from "react-feather"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

function PartyLedgerView({
  data = [],
  loading = false,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  selectedItemIds = [],
  openItemModal,
  onRefresh,
  onRowDoubleClick,
  onViewContracts,
  setShowQtyDiffModal,
}) {
  const [partySearch, setPartySearch] = useState("")
  const [stationFilter, setStationFilter] = useState("all")
  
  // Dedicated Party Filters
  const [tradeTypeFilter, setTradeTypeFilter] = useState("all") // "all" | "buy" | "sell"
  const [liftingFilter, setLiftingFilter] = useState("all") // "all" | "pending" | "lifted"
  const [balanceFilter, setBalanceFilter] = useState("all") // "all" | "pos" | "neg" | "zero"

  const [selectedRows, setSelectedRows] = useState(new Set())

  // Unique stations for quick station dropdown
  const uniqueStations = useMemo(() => {
    const set = new Set()
    data.forEach(r => {
      if (r.Station && r.Station.trim()) set.add(r.Station.trim())
    })
    return Array.from(set).sort()
  }, [data])

  // Filter and sort Party data
  const filteredData = useMemo(() => {
    let result = Array.isArray(data) ? [...data] : []

    // Search filter
    if (partySearch.trim()) {
      const q = partySearch.toLowerCase().trim()
      result = result.filter(row => {
        const name = (row.LedgerName || "").toLowerCase()
        const st = (row.Station || "").toLowerCase()
        const person = (row.Person || "").toLowerCase()
        const mob = (row.MobileNo || "").toLowerCase()
        return name.includes(q) || st.includes(q) || person.includes(q) || mob.includes(q)
      })
    }

    // Station filter
    if (stationFilter !== "all") {
      result = result.filter(row => (row.Station || "").trim() === stationFilter)
    }

    // Trade Type Filter (BUY only / SELL only)
    if (tradeTypeFilter === "buy") {
      result = result.filter(row => (parseFloat(row.PurchaseQty) || 0) > 0)
    } else if (tradeTypeFilter === "sell") {
      result = result.filter(row => (parseFloat(row.SaleQty) || 0) > 0)
    }

    // Lifting Status Filter
    if (liftingFilter === "pending") {
      result = result.filter(row => (parseFloat(row.PendingQty) || 0) > 0.001)
    } else if (liftingFilter === "lifted") {
      result = result.filter(row => (parseFloat(row.PendingQty) || 0) <= 0.001)
    }

    // Balance Filter
    if (balanceFilter === "pos") {
      result = result.filter(row => (parseFloat(row.TradeBalance ?? row.Total) || 0) > 0.001)
    } else if (balanceFilter === "neg") {
      result = result.filter(row => (parseFloat(row.TradeBalance ?? row.Total) || 0) < -0.001)
    } else if (balanceFilter === "zero") {
      result = result.filter(row => Math.abs(parseFloat(row.TradeBalance ?? row.Total) || 0) <= 0.001)
    }

    // Sort alphabetically by Party Name
    return result.sort((a, b) => {
      const nameA = (a.LedgerName || "").toLowerCase()
      const nameB = (b.LedgerName || "").toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }, [data, partySearch, stationFilter, tradeTypeFilter, liftingFilter, balanceFilter])

  // Derived selectAll state
  const isAllSelected = useMemo(() => {
    return filteredData.length > 0 && filteredData.every(r => selectedRows.has(String(r.LedgerId)))
  }, [filteredData, selectedRows])

  // Toggle select all handler
  const handleToggleSelectAll = useCallback(() => {
    setSelectedRows(prev => {
      const allSelected = filteredData.length > 0 && filteredData.every(r => prev.has(String(r.LedgerId)))
      if (allSelected) {
        return new Set()
      } else {
        return new Set(filteredData.map(r => String(r.LedgerId)))
      }
    })
  }, [filteredData])

  // Single row toggle handler
  const handleToggleRow = useCallback((ledgerId) => {
    const normId = String(ledgerId)
    setSelectedRows(prev => {
      const next = new Set(prev)
      if (next.has(normId)) {
        next.delete(normId)
      } else {
        next.add(normId)
      }
      return next
    })
  }, [])

  // Calculate totals for selected rows (or all visible rows)
  const totals = useMemo(() => {
    const isSelection = selectedRows.size > 0
    const targetRows = isSelection
      ? filteredData.filter(r => selectedRows.has(String(r.LedgerId)))
      : filteredData

    let totalPurchaseQty = 0
    let totalSaleQty = 0
    let totalDealValue = 0
    let totalLiftedQty = 0
    let totalPendingQty = 0
    let totalTradeBalance = 0
    let totalContracts = 0
    let buyWeightedAmt = 0
    let sellWeightedAmt = 0

    targetRows.forEach(row => {
      const pQty = parseFloat(row.PurchaseQty ?? 0) || 0
      const sQty = parseFloat(row.SaleQty ?? 0) || 0
      const pRate = parseFloat(row.AvgPurchaseRate ?? 0) || 0
      const sRate = parseFloat(row.AvgSaleRate ?? 0) || 0
      const dealVal = parseFloat(row.TotalDealValue ?? 0) || 0
      const lifted = parseFloat(row.TotalLiftedQty ?? 0) || 0
      const pending = parseFloat(row.PendingQty ?? 0) || 0
      const bal = parseFloat(row.TradeBalance ?? row.Total ?? 0) || 0
      const cCount = parseInt(row.TotalContracts ?? 1, 10) || 1

      totalPurchaseQty += pQty
      totalSaleQty += sQty
      totalDealValue += dealVal
      totalLiftedQty += lifted
      totalPendingQty += pending
      totalTradeBalance += bal
      totalContracts += cCount
      buyWeightedAmt += pQty * pRate
      sellWeightedAmt += sQty * sRate
    })

    const avgBuyRate = totalPurchaseQty > 0 ? buyWeightedAmt / totalPurchaseQty : 0
    const avgSellRate = totalSaleQty > 0 ? sellWeightedAmt / totalSaleQty : 0

    return {
      isSelection,
      count: targetRows.length,
      totalCount: filteredData.length,
      totalContracts,
      totalPurchaseQty,
      avgBuyRate,
      totalSaleQty,
      avgSellRate,
      totalDealValue,
      totalLiftedQty,
      totalPendingQty,
      totalTradeBalance,
    }
  }, [filteredData, selectedRows])

  return (
    <div className="party-ledger-view d-flex flex-column h-100" style={{ minHeight: 0 }}>
      {/* DESKTOP KPI CARDS (Hidden on Mobile for screen space) */}
      <div className="d-none d-md-block bg-light p-2 border-bottom" style={{ flexShrink: 0 }}>
        <Row className="g-2 text-center">
          <Col md={2}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: "#ffffff", borderLeft: "4px solid #16a34a" }}>
              <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>Active Parties</div>
              <div className="fw-bold text-success" style={{ fontSize: "0.95rem" }}>
                {totals.isSelection ? `${totals.count}/${totals.totalCount}` : totals.totalCount}
                <span className="text-muted fw-normal ms-1" style={{ fontSize: "0.65rem" }}>({totals.totalContracts} Saudey)</span>
              </div>
            </div>
          </Col>

          <Col md={2}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: "#ffffff", borderLeft: "4px solid #2563eb" }}>
              <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>Purchase Qty</div>
              <div className="fw-bold text-primary" style={{ fontSize: "0.95rem" }}>
                {totals.totalPurchaseQty.toFixed(1)} <span style={{ fontSize: "0.65rem", color: "#64748b" }}>MT</span>
              </div>
              <div style={{ fontSize: "0.65rem", color: "#64748b" }}>
                Avg: ₹{totals.avgBuyRate.toFixed(0)}
              </div>
            </div>
          </Col>

          <Col md={2}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: "#ffffff", borderLeft: "4px solid #0891b2" }}>
              <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>Sale Qty</div>
              <div className="fw-bold text-info" style={{ fontSize: "0.95rem" }}>
                {totals.totalSaleQty.toFixed(1)} <span style={{ fontSize: "0.65rem", color: "#64748b" }}>MT</span>
              </div>
              <div style={{ fontSize: "0.65rem", color: "#64748b" }}>
                Avg: ₹{totals.avgSellRate.toFixed(0)}
              </div>
            </div>
          </Col>

          <Col md={3}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: "#ffffff", borderLeft: "4px solid #7c3aed" }}>
              <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>Total Deal Value</div>
              <div className="fw-bold" style={{ fontSize: "0.95rem", color: "#7c3aed" }}>
                ₹{totals.totalDealValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </Col>

          <Col md={3}>
            <div className="card shadow-sm border-0 py-1 px-2 h-100" style={{ background: totals.totalPendingQty > 0 ? "#fffbeb" : "#f0fdf4", borderLeft: `4px solid ${totals.totalPendingQty > 0 ? '#d97706' : '#15803d'}` }}>
              <div style={{ fontSize: "0.68rem", color: totals.totalPendingQty > 0 ? "#b45309" : "#166534", fontWeight: "600" }}>
                Lifting: Lifted vs Pending
              </div>
              <div className="fw-bold" style={{ fontSize: "0.95rem" }}>
                <span className="text-success">{totals.totalLiftedQty.toFixed(1)} MT</span>
                <span className="text-muted mx-1">/</span>
                <span className={totals.totalPendingQty > 0 ? "text-warning fw-bolder text-dark" : "text-muted"}>
                  {totals.totalPendingQty.toFixed(1)} MT
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* MOBILE COMPACT KPI STRIP (Ultra-compact 2-tier pill strip so table gets max screen height) */}
      <div className="d-block d-md-none bg-light px-2 py-1 border-bottom" style={{ flexShrink: 0 }}>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div className="d-flex align-items-center gap-1">
            <span className="badge bg-success" style={{ fontSize: "0.68rem" }}>
              Parties: {totals.isSelection ? `${totals.count}/${totals.totalCount}` : totals.totalCount} ({totals.totalContracts})
            </span>
            <span className="badge bg-primary" style={{ fontSize: "0.68rem" }}>
              Buy: {totals.totalPurchaseQty.toFixed(0)} MT
            </span>
            <span className="badge bg-info" style={{ fontSize: "0.68rem" }}>
              Sell: {totals.totalSaleQty.toFixed(0)} MT
            </span>
          </div>
          <span className="badge bg-purple text-white" style={{ background: "#7c3aed", fontSize: "0.68rem" }}>
            Val: ₹{(totals.totalDealValue / 100000).toFixed(1)}L
          </span>
        </div>
        <div className="d-flex justify-content-between align-items-center" style={{ fontSize: "0.7rem" }}>
          <span>Lifted: <strong className="text-success">{totals.totalLiftedQty.toFixed(1)} MT</strong></span>
          <span className="fw-bold">
            Pending: <span className={totals.totalPendingQty > 0 ? "badge bg-warning text-dark" : "badge bg-success"}>{totals.totalPendingQty.toFixed(1)} MT</span>
          </span>
        </div>
      </div>

      {/* DEDICATED PARTY FILTER BAR (Responsive Horizontal Scroll) */}
      <div className="bg-white px-2 py-1 border-bottom shadow-sm" style={{ flexShrink: 0 }}>
        <div className="d-flex align-items-center flex-wrap gap-2" style={{ overflowX: "auto" }}>
          {/* Search Party Name / Contact / Station */}
          <div className="position-relative" style={{ minWidth: "160px", maxWidth: "230px", flex: "1 1 auto" }}>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search Party, Station, Contact..."
              value={partySearch}
              onChange={e => setPartySearch(e.target.value)}
              style={{
                height: "32px",
                fontSize: "0.75rem",
                paddingLeft: "26px",
                borderRadius: "6px",
                border: "1px solid #94a3b8",
              }}
            />
            <Search
              size={13}
              style={{ position: "absolute", left: "8px", top: "10px", color: "#64748b" }}
            />
            {partySearch && (
              <X
                size={13}
                onClick={() => setPartySearch("")}
                style={{ position: "absolute", right: "8px", top: "10px", color: "#94a3b8", cursor: "pointer" }}
              />
            )}
          </div>

          {/* Trade Type Filter (All, BUY, SELL) */}
          <div style={{ width: "120px", flexShrink: 0 }}>
            <Form.Select
              size="sm"
              value={tradeTypeFilter}
              onChange={e => setTradeTypeFilter(e.target.value)}
              style={{ height: "32px", fontSize: "0.73rem", borderRadius: "6px", border: "1px solid #94a3b8" }}
            >
              <option value="all">All Trades (All)</option>
              <option value="buy">🛒 BUY (खरीद)</option>
              <option value="sell">🏷️ SELL (बिक्री)</option>
            </Form.Select>
          </div>

          {/* Lifting Status Filter */}
          <div style={{ width: "135px", flexShrink: 0 }}>
            <Form.Select
              size="sm"
              value={liftingFilter}
              onChange={e => setLiftingFilter(e.target.value)}
              style={{ height: "32px", fontSize: "0.73rem", borderRadius: "6px", border: "1px solid #94a3b8" }}
            >
              <option value="all">Lifting: All</option>
              <option value="pending">⏳ Pending (बाकी)</option>
              <option value="lifted">✅ Lifted (पूर्ण)</option>
            </Form.Select>
          </div>

          {/* Balance Filter */}
          <div style={{ width: "125px", flexShrink: 0 }}>
            <Form.Select
              size="sm"
              value={balanceFilter}
              onChange={e => setBalanceFilter(e.target.value)}
              style={{ height: "32px", fontSize: "0.73rem", borderRadius: "6px", border: "1px solid #94a3b8" }}
            >
              <option value="all">Balance: All</option>
              <option value="pos">Debit (लेना &gt; 0)</option>
              <option value="neg">Credit (देना &lt; 0)</option>
              <option value="zero">Nil (= 0)</option>
            </Form.Select>
          </div>

          {/* Station Filter */}
          {uniqueStations.length > 0 && (
            <div style={{ width: "115px", flexShrink: 0 }}>
              <Form.Select
                size="sm"
                value={stationFilter}
                onChange={e => setStationFilter(e.target.value)}
                style={{ height: "32px", fontSize: "0.73rem", borderRadius: "6px", border: "1px solid #94a3b8" }}
              >
                <option value="all">All Stations</option>
                {uniqueStations.map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </Form.Select>
            </div>
          )}

          {/* Date Picker Range */}
          <div className="d-flex align-items-center gap-1" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
            <div style={{ width: "95px" }}>
              <DatePicker
                selected={fromDate ? new Date(fromDate) : null}
                onChange={date => setFromDate(date)}
                className="form-control form-control-sm"
                dateFormat="dd/MM/yyyy"
                placeholderText="From Date"
                portalId="root-portal"
                popperPlacement="bottom-start"
                customInput={<input style={{ height: "32px", fontSize: "0.72rem", borderRadius: "6px" }} />}
              />
            </div>
            <span style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: "600" }}>To</span>
            <div style={{ width: "95px" }}>
              <DatePicker
                selected={toDate ? new Date(toDate) : null}
                onChange={date => setToDate(date)}
                className="form-control form-control-sm"
                dateFormat="dd/MM/yyyy"
                placeholderText="To Date"
                portalId="root-portal"
                popperPlacement="bottom-start"
                customInput={<input style={{ height: "32px", fontSize: "0.72rem", borderRadius: "6px" }} />}
              />
            </div>
          </div>

          {/* Item Filter Button */}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={openItemModal}
            style={{
              height: "32px",
              fontSize: "0.72rem",
              borderRadius: "6px",
              whiteSpace: "nowrap",
              fontWeight: "600",
              flexShrink: 0,
            }}
          >
            📦 Items {selectedItemIds && selectedItemIds.length > 0 ? `(${selectedItemIds.length})` : "(All)"}
          </Button>

          {/* Refresh Button */}
          <Button
            variant="light"
            size="sm"
            onClick={onRefresh}
            title="Refresh Party Ledger"
            style={{ height: "32px", border: "1px solid #cbd5e1", borderRadius: "6px", flexShrink: 0 }}
          >
            <RefreshCw size={13} className={loading ? "spin" : ""} />
          </Button>
        </div>
      </div>

      {/* Main Table Area with dedicated scrolling */}
      <div className="table-scroll-container flex-grow-1" style={{ overflowY: "auto", overflowX: "auto" }}>
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="success" />
            <div className="mt-2 text-muted fw-semibold" style={{ fontSize: "0.85rem" }}>
              Loading Party Trade & Account Ledger...
            </div>
          </div>
        ) : filteredData.length > 0 ? (
          <Table bordered hover size="sm" className="mb-0" style={{ minWidth: "1280px", tableLayout: "auto" }}>
            <thead className="table-success text-center" style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                <th
                  style={{ width: "42px", minWidth: "42px", whiteSpace: "nowrap", cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggleSelectAll()
                  }}
                  title={isAllSelected ? "Deselect All Parties" : "Select All Parties"}
                >
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    readOnly
                    className="form-check-input"
                    title={isAllSelected ? "Deselect All Parties" : "Select All Parties"}
                    style={{ cursor: "pointer", pointerEvents: "none" }}
                  />
                </th>
                <th style={{ minWidth: "220px", textAlign: "left", whiteSpace: "nowrap" }}>Party Name (Supplier / Buyer)</th>
                <th style={{ minWidth: "110px", textAlign: "left", whiteSpace: "nowrap" }}>Station</th>
                <th style={{ minWidth: "70px", textAlign: "center", whiteSpace: "nowrap" }}>Saudey</th>
                <th style={{ minWidth: "100px", textAlign: "right", whiteSpace: "nowrap" }}>Buy Qty (MT)</th>
                <th style={{ minWidth: "100px", textAlign: "right", whiteSpace: "nowrap" }}>Avg Buy Rate</th>
                <th style={{ minWidth: "100px", textAlign: "right", whiteSpace: "nowrap" }}>Sell Qty (MT)</th>
                <th style={{ minWidth: "100px", textAlign: "right", whiteSpace: "nowrap" }}>Avg Sell Rate</th>
                <th style={{ minWidth: "130px", textAlign: "right", whiteSpace: "nowrap" }}>Total Deal Value (₹)</th>
                <th style={{ minWidth: "100px", textAlign: "right", whiteSpace: "nowrap" }}>Lifted (MT)</th>
                <th style={{ minWidth: "110px", textAlign: "right", whiteSpace: "nowrap" }}>Pending (MT)</th>
                <th style={{ minWidth: "120px", textAlign: "right", whiteSpace: "nowrap" }}>Trade Balance</th>
                <th style={{ minWidth: "95px", textAlign: "center", whiteSpace: "nowrap" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map(row => {
                const normId = String(row.LedgerId)
                const pending = parseFloat(row.PendingQty ?? 0) || 0
                const tradeBal = parseFloat(row.TradeBalance ?? row.Total ?? 0) || 0
                const isSelected = selectedRows.has(normId)
                const rowBg = isSelected ? "#f0fdf4" : (pending > 0 ? "#ffffff" : "#f8fafc")

                return (
                  <tr
                    key={`party-${normId}`}
                    onDoubleClick={() => onRowDoubleClick(row, selectedRows)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: rowBg,
                      transition: "background-color 0.15s ease",
                    }}
                    title="Double-click to view ledger details"
                  >
                    <td
                      className="text-center align-middle"
                      style={{ cursor: "pointer", whiteSpace: "nowrap", width: "42px" }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleToggleRow(normId)
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="form-check-input"
                        style={{ cursor: "pointer", pointerEvents: "none" }}
                      />
                    </td>
                    <td className="align-middle fw-semibold text-dark" style={{ whiteSpace: "nowrap" }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span>{row.LedgerName}</span>
                        {row.Person && (
                          <span className="badge bg-light text-muted ms-1" style={{ fontSize: "0.65rem", fontWeight: "normal" }}>
                            {row.Person}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="align-middle text-muted" style={{ whiteSpace: "nowrap" }}>
                      {row.Station ? (
                        <span><MapPin size={11} className="me-1 text-secondary" />{row.Station}</span>
                      ) : "-"}
                    </td>
                    <td className="align-middle text-center font-monospace" style={{ whiteSpace: "nowrap" }}>
                      <span className="badge bg-secondary" style={{ fontSize: "0.7rem" }}>
                        {row.TotalContracts ?? 1}
                      </span>
                    </td>
                    <td className="align-middle text-end font-monospace" style={{ whiteSpace: "nowrap" }}>
                      {(parseFloat(row.PurchaseQty) || 0).toFixed(2)}
                    </td>
                    <td className="align-middle text-end font-monospace text-muted" style={{ whiteSpace: "nowrap" }}>
                      {(parseFloat(row.AvgPurchaseRate) || 0) > 0 ? `₹${(parseFloat(row.AvgPurchaseRate) || 0).toFixed(0)}` : "-"}
                    </td>
                    <td className="align-middle text-end font-monospace" style={{ whiteSpace: "nowrap" }}>
                      {(parseFloat(row.SaleQty) || 0).toFixed(2)}
                    </td>
                    <td className="align-middle text-end font-monospace text-muted" style={{ whiteSpace: "nowrap" }}>
                      {(parseFloat(row.AvgSaleRate) || 0) > 0 ? `₹${(parseFloat(row.AvgSaleRate) || 0).toFixed(0)}` : "-"}
                    </td>
                    <td className="align-middle text-end font-monospace fw-bold" style={{ color: "#4338ca", whiteSpace: "nowrap" }}>
                      ₹{(parseFloat(row.TotalDealValue) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="align-middle text-end font-monospace fw-bold text-success" style={{ whiteSpace: "nowrap" }}>
                      {(parseFloat(row.TotalLiftedQty) || 0).toFixed(2)}
                    </td>
                    <td className="align-middle text-end font-monospace" style={{ whiteSpace: "nowrap" }}>
                      <Badge
                        bg={pending > 0 ? "warning" : "success"}
                        text={pending > 0 ? "dark" : "light"}
                        style={{ fontSize: "0.74rem", padding: "4px 7px" }}
                      >
                        {pending.toFixed(2)} MT
                      </Badge>
                    </td>
                    <td className="align-middle text-end font-monospace fw-bold" style={{ whiteSpace: "nowrap" }}>
                      <span style={{ color: tradeBal > 0 ? "#15803d" : (tradeBal < 0 ? "#dc2626" : "#64748b") }}>
                        ₹{tradeBal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="text-center align-middle" onClick={e => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="py-0 px-2 fw-semibold"
                        style={{ fontSize: "0.72rem", height: "24px" }}
                        onClick={() => onViewContracts ? onViewContracts(row) : onRowDoubleClick(row)}
                      >
                        View Sauda
                      </Button>
                    </td>
                  </tr>
                )
              })}

              {/* Totals Row - Sticky bottom for Desktop */}
              <tr className="totals-row d-none d-md-table-row" style={{ backgroundColor: "#000000", color: "#ffffff", position: "sticky", bottom: 0, zIndex: 6 }}>
                <td className="text-center fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: "0.72rem" }}>
                    {totals.isSelection ? `${totals.count}/${totals.totalCount}` : totals.totalCount}
                  </span>
                </td>
                <td className="fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  TOTAL ({totals.isSelection ? "Selected" : "All Parties"})
                </td>
                <td style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>-</td>
                <td className="text-center fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="secondary">{totals.totalContracts}</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="primary">{totals.totalPurchaseQty.toFixed(2)}</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: "0.75rem", color: "#93c5fd" }}>₹{totals.avgBuyRate.toFixed(0)}</span>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="info">{totals.totalSaleQty.toFixed(2)}</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: "0.75rem", color: "#67e8f9" }}>₹{totals.avgSellRate.toFixed(0)}</span>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="light" text="dark">₹{totals.totalDealValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg="success">{totals.totalLiftedQty.toFixed(2)} MT</Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <Badge bg={totals.totalPendingQty > 0 ? "warning" : "success"} text={totals.totalPendingQty > 0 ? "dark" : "light"}>
                    {totals.totalPendingQty.toFixed(2)} MT
                  </Badge>
                </td>
                <td className="text-end fw-bold" style={{ backgroundColor: "#000000", color: "#ffffff", whiteSpace: "nowrap" }}>
                  <span style={{ fontSize: "0.75rem", color: "#86efac" }}>
                    ₹{totals.totalTradeBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </td>
                <td style={{ backgroundColor: "#000000", whiteSpace: "nowrap" }}>-</td>
              </tr>
            </tbody>
          </Table>
        ) : (
          <div className="text-center p-5 text-muted">
            <AlertCircle size={40} className="mb-2 text-warning" />
            <h6 className="fw-bold">No Party Records Found</h6>
            <p style={{ fontSize: "0.8rem" }}>
              No parties matched the selected trade filters or date range.
            </p>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setPartySearch("")
                setStationFilter("all")
                setTradeTypeFilter("all")
                setLiftingFilter("all")
                setBalanceFilter("all")
              }}
            >
              Reset Filters
            </Button>
          </div>
        )}
      </div>

      {/* Mobile Sticky Bottom Summary Bar */}
      <div
        className="d-block d-md-none"
        style={{
          flexShrink: 0,
          backgroundColor: "#064e3b",
          borderTop: "2px solid #10b981",
          padding: "5px 8px",
          color: "#ffffff",
          zIndex: 25,
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-1">
          <div className="d-flex align-items-center gap-1">
            <span className="badge bg-primary" style={{ fontSize: "0.68rem" }}>
              Buy: {totals.totalPurchaseQty.toFixed(0)} MT
            </span>
            <span className="badge bg-info" style={{ fontSize: "0.68rem" }}>
              Sell: {totals.totalSaleQty.toFixed(0)} MT
            </span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <span className="badge bg-light text-dark" style={{ fontSize: "0.68rem" }}>
              Val: ₹{(totals.totalDealValue / 100000).toFixed(1)}L
            </span>
            <span
              className={`badge ${totals.totalPendingQty > 0 ? 'bg-warning text-dark' : 'bg-success'}`}
              style={{ fontSize: "0.72rem", fontWeight: "bold" }}
            >
              Pending: {totals.totalPendingQty.toFixed(1)} MT
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PartyLedgerView
